// The glass of a slot. A slot of a caster is a vessel, and its fill is the
// liquid: one outline for each family of spell, and one for a scroll.
// Added 2026-10-12. A slot was a round square, as a face of the wheel is.
// Garrett: "a deesign pass on how to make these look more like \"magic
// spells\" in shape? maybe like a design pass as geometric glass beakers?"
// He chose the vessel for each family from three drawn options: "yeah do C,
// and yeah treat scrolls as a special vessel shape basically?"
//
// Every outline is drawn in one box, so a row of slots has one height. The
// face of a slot wears the outline as a mask, so the fill, the glint, and the
// shadow of the slot keep their CSS and take the shape.

import type { SpellFamily } from '../spells/types';

/** The box every outline is drawn in. A slot has this ratio. */
const BOX_WIDTH = 60;
const BOX_HEIGHT = 72;

type VesselShape = {
  /** The outline, in the box. */
  path: string;
  /** The height the liquid stops at. The neck over it stays glass. */
  liquidTop: number;
  /** The middle of the icon, and its side. */
  icon: { x: number; y: number; size: number };
  /** The span of the mouth. The sparks of a slot leave from it. */
  mouth: readonly [number, number];
};

/** Names of the outlines are plain nouns. No name here is a name the player sees. */
const VESSEL_OF_FAMILY: Record<SpellFamily, VesselShape> = {
  placement: {
    path: 'M5 4h50v5h-3v55a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4V9H5z',
    liquidTop: 12,
    icon: { x: 30, y: 40, size: 38 },
    mouth: [8, 52],
  },
  royalty: {
    path: 'M22 2h16v6h-2v8l22 16v20L36 68H24L2 52V32l22-16V8h-2z',
    liquidTop: 18,
    icon: { x: 30, y: 43, size: 34 },
    mouth: [22, 38],
  },
  ability: {
    path: 'M20 2h20v6h-2v16l20 38a4 4 0 0 1-3.5 6H5.5a4 4 0 0 1-3.5-6l20-38V8h-2z',
    liquidTop: 24,
    icon: { x: 30, y: 49, size: 30 },
    mouth: [20, 40],
  },
  status: {
    path: 'M21 2h18v6h-2v13.5a24 24 0 1 1-14 0V8h-2z',
    liquidTop: 21,
    icon: { x: 30, y: 45, size: 33 },
    mouth: [21, 39],
  },
  timer: {
    path: 'M22 2h16v6h-2v8l20 14v32a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V30l20-14V8h-2z',
    liquidTop: 18,
    icon: { x: 30, y: 46, size: 36 },
    mouth: [22, 38],
  },
  ground: {
    path: 'M14 2h32v7h-4v5h8a6 6 0 0 1 6 6v42a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V20a6 6 0 0 1 6-6h8V9h-4z',
    liquidTop: 16,
    icon: { x: 30, y: 42, size: 38 },
    mouth: [14, 46],
  },
};

/** A scroll is in a case with a cap at each end, whatever the family of its spell. */
const SCROLL_CASE: VesselShape = {
  path: 'M6 2h48v9h-4v50h4v9H6v-9h4V11H6z',
  liquidTop: 11,
  icon: { x: 30, y: 36, size: 34 },
  mouth: [10, 50],
};

const maskOf = (markup: string): string =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BOX_WIDTH} ${BOX_HEIGHT}" preserveAspectRatio="none">${markup}</svg>`,
  )}")`;

const percent = (part: number, whole: number): string => `${((part / whole) * 100).toFixed(2)}%`;

/** The custom properties `caster.css` reads to draw a slot as this vessel. They go on the list item of the slot. */
const styleOf = (shape: VesselShape): string => {
  const { icon, mouth, liquidTop } = shape;
  return [
    `--vessel-ratio: ${BOX_WIDTH} / ${BOX_HEIGHT}`,
    `--vessel-mask: ${maskOf(`<path d="${shape.path}"/>`)}`,
    `--liquid-span: ${percent(BOX_HEIGHT - liquidTop, BOX_HEIGHT)}`,
    `--icon-left: ${percent(icon.x - icon.size / 2, BOX_WIDTH)}`,
    `--icon-top: ${percent(icon.y - icon.size / 2, BOX_HEIGHT)}`,
    `--icon-width: ${percent(icon.size, BOX_WIDTH)}`,
    `--mouth-left: ${percent(mouth[0], BOX_WIDTH)}`,
    `--mouth-right: ${percent(BOX_WIDTH - mouth[1], BOX_WIDTH)}`,
  ].join('; ');
};

const FAMILY_STYLES = Object.fromEntries(
  Object.entries(VESSEL_OF_FAMILY).map(([family, shape]) => [family, styleOf(shape)]),
) as Record<SpellFamily, string>;
const SCROLL_STYLE = styleOf(SCROLL_CASE);

/** The style of the vessel of a slot. A scroll slot is a case, with a scroll or with none. */
export const vesselStyle = (kind: 'skill' | 'scroll', family: SpellFamily | null): string =>
  kind === 'scroll' || family === null ? SCROLL_STYLE : FAMILY_STYLES[family];
